import React, { useState, useEffect, useMemo, useCallback, useRef, useLayoutEffect } from "react";
import { FluentInput } from "../../../../components/FluentUI/Fluent-Input";
import { FluentInputArea } from "../../../../components/FluentUI/Fluent-InputArea";
import { FluentDropdown } from "../../../../components/FluentUI/Fluent-Dropdown";
import { FluentDatePicker } from "../../../../components/FluentUI/Fluent-DatePicker";
import { Formik } from "formik";
import TaskFormFooter from "./task-form-footer";
import TaskFormHeader from "./task-form-header";
import PropTypes from "prop-types";
import { renderTaskDetailDynamicFields } from "../task-create-edit/helpers/task-helpers";
import {
  GetValue,
  InitializeForm,
  IsChangeEvent,
  FieldTypes,
  processFieldConditions,
  isTextLikeField
} from "./task-form-util";
import {
  isParent,
  byOrder,
  buildUnits,
} from "../../../admin/scenes/task-types/new-task-type/form-design/form-design-util";
import { FluentLabel } from "../../../../components/FluentUI/Fluent-Label";
import { FluentSwitch } from "../../../../components/FluentUI/Fluent-Switch";
import { FluentInputAreaButtonComboBox } from "../../../../components/FluentUI/Fluent-InputArea-Button";
import { FluentMultiSelectCheckboxes } from "../../../../components/FluentUI/Fluent-Multi-Select-Checkboxes";
import { FileField } from "../../../../components/FluentUI/FileField";
import TelerikDataGrid from "../../../../components/DynamicField/Tasks/Form/TelerikDataGrid";
import { FluentRadio } from "../../../../components/FluentUI/Fluent-Radio";
import { FluentInputUrl } from "../../../../components/FluentUI/Fluent-Input-Url";
import FluentUiIcon from "../../../../assets/fluent-ui-icon";

const showJsonTables = window["APP_SETTINGS"].SHOW_JSON_TABLES;

const TaskFormFields = ({
  currentSubForm,
  SaveSubForm,
  totalForms,
  ResetFormSection,
  SetDataWithSameApiName,
  AutoPopulateSubForm,
  AutoPopulateCount,
  handleCustomFormulaButtonClick,
  IsView,
  submitClicked,
  taskId,
  formLabel,
  formStatusMap
}) => {
  const [formFields, setFormFields] = useState([]);
  const [expanded, setExpanded] = useState(() => new Set());
  const functionMap = {
    handleCustomFormulaButtonClick: handleCustomFormulaButtonClick,
  };

  const headerRefs = useRef({});
  const scrollerRef = useRef(null);
  const pendingAnchorRef = useRef(null);

  const setHeaderRef = (id) => (el) => {
    headerRefs.current[id] = el;
  };

  const toggle = useCallback((id) => {
    const scroller = scrollerRef.current;
    const header = headerRefs.current[id];

    const prevTop = (scroller && header)
      ? header.offsetTop - scroller.scrollTop
      : null;

    pendingAnchorRef.current = { id, prevTop };

    setExpanded(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  }, []);

  useLayoutEffect(() => {
    const scroller = scrollerRef.current;
    const pending = pendingAnchorRef.current;
    if (!scroller || !pending) return;

    const { id, prevTop } = pending;
    const header = headerRefs.current[id];
    pendingAnchorRef.current = null;

    if (!header || prevTop == null) return;

    const newTop = header.offsetTop - scroller.scrollTop;
    scroller.scrollTop += (newTop - prevTop);
  }, [expanded]);


  useEffect(() => {
    if (currentSubForm) {
      setFormFields(currentSubForm.Fields);
    }
  }, [currentSubForm]);

  const setDynamicFields = (field) => {
    SetDataWithSameApiName(field, field.Table);
  };

  const LoadFormField = (field, values) => {
    if (formFields.length > 0) {
      let fieldComponent;
      const sectionNotEditable = currentSubForm?.IsEditable === false;
      const sectionMandatory = currentSubForm?.IsMandatory === true;
      let fieldComponentProps = {
        id: `field-${field.Id}`,
        label: field.LocalLabel ? field.LocalLabel : field.DefaultLabel,
        required: field.IsRequired || sectionMandatory,
        defaultValue: values[`${field.Id}`],
        value: values[`${field.Id}`],
        disabled: IsView || field.IsDisabled || sectionNotEditable,
        validationMessage:
          submitClicked &&
            (field.IsRequired || sectionMandatory) &&
            field.IsVisible &&
            (field.FieldType === FieldTypes.file ? (!field.Files || field.Files.length === 0)
              : (!values[`${field.Id}`] ||
                String(values[`${field.Id}`]).trim() === ""))
            ? "Field is Required"
            : "",
        defaultFiles: field?.Files ?? [],
        noKey: true,
      };

      processFieldConditions(
        field,
        formFields,
        fieldComponentProps,
        SetDataWithSameApiName,
        IsView
      );
      switch (field.FieldType) {
        case FieldTypes.ShortTextEntry:
          fieldComponent = FluentInput;
          break;
        case FieldTypes.LongTextEntry:
          fieldComponent = FluentInputArea;
          break;
        case FieldTypes.Dropdown:
          fieldComponent = FluentDropdown;
          const opts = field.LookUpValues.split("|").map(o => String(o).trim());
          const selection = values?.[`${field.Id}`];
          const selectionNormalized = selection != null ? String(selection).trim() : "";
          fieldComponentProps.options = opts;
          fieldComponentProps.isControlled = true;
          fieldComponentProps.selectedOptions = selectionNormalized ? [selectionNormalized] : [];
          fieldComponentProps.value = selectionNormalized || "";
          break;
        case FieldTypes.Date:
          fieldComponent = FluentDatePicker;
          fieldComponentProps.value = values[`${field.Id}`]
            ? new Date(values[`${field.Id}`])
            : "";
          break;
        case FieldTypes.Toggle:
          fieldComponentProps.checked = values[`${field.Id}`] === "selected";
          fieldComponent = FluentSwitch;
          break;
        case FieldTypes.NumericDecimal:
        case FieldTypes.NumericInteger:
          fieldComponentProps.type = "number";
          fieldComponent = FluentInput;
          break;
        case FieldTypes.Checkbox:
          fieldComponentProps.options = field.LookUpValues.split("|");
          fieldComponentProps.defaultValue = values[`${field.Id}`];
          fieldComponent = FluentMultiSelectCheckboxes;
          break;
        case FieldTypes.TextButtonComboBox: {
          const [iconName, functionName, buttonText] =
            field.LookUpValues.split("|");
          fieldComponent = FluentInputAreaButtonComboBox;
          fieldComponentProps.icon = iconName;
          fieldComponentProps.onClick = functionMap[functionName]
            ? () => functionMap[functionName](field)
            : () => console.log("Function not found");
          fieldComponentProps.buttonText = buttonText;
          fieldComponentProps.buttonDisabled =
            fieldComponentProps.buttonDisabled ?? field.IsDisabled;
          fieldComponentProps.noKey = false;
          break;
        }
        case FieldTypes.file:
          fieldComponent = FileField;
          break;
        case FieldTypes.Radio:
          fieldComponentProps.options = field.LookUpValues.split("|");
          fieldComponentProps.type = "dynamicField";
          fieldComponent = FluentRadio;
          break;
        case FieldTypes.URL: {
          fieldComponent = FluentInputUrl;
          fieldComponentProps.protocol = field.LookUpValues?.trim() || "https://";
          fieldComponentProps.type = "url";
          break;
        }
        default:
          fieldComponent = FluentInput;
          break;
      }

      return React.createElement(fieldComponent, fieldComponentProps);
    }
  };

  const renderField = (field, values, setFieldValue, setFieldTouched) => {
    if (
      field.FieldType === FieldTypes.Table &&
      showJsonTables === "TRUE" &&
      field?.Table?.JsonDataFlag === true
    ) {
      field.IsDataGrid = true;
      return (
        <div className="form-field" key={field.Id} data-testid="form-field">
          <FluentLabel
            label={field.LocalLabel}
            className="float-left"
            data-testid={`field-label-${field.Id}`}
          />
          <TelerikDataGrid
            jsonId={field.Table.JsonDataId}
            jsonGridDefinition={field.Table.JsonDataGridDefinitionList}
            jsonFilter={field.Table.JsonDataFilter}
            jsonDataNoEdit={field.Table.JsonDataNoEdit}
            IsView={IsView}
          />
        </div>
      );
    }

    if (field.FieldType === FieldTypes.Table) {
      return (
        <div className="form-field" key={field.Id} data-testid="form-field">
          <FluentLabel
            label={field.LocalLabel}
            className="float-left"
            data-testid={`field-label-${field.Id}`}
          />
          {renderTaskDetailDynamicFields({
            dynamicFields: [field],
            setDynamicFields,
            disableErrors: true,
            disabled: false,
            disabledAddDelete:
              field.IsDisabled || IsView || currentSubForm?.IsEditable === false,
            taskWithWorkflow: [],
            isForm: true,
            taskId: taskId,
          })}
        </div>
      );
    }

    const formField = LoadFormField(field, values);
    return (
      <div
        className="form-field d-flex flex-column align-items-start"
        key={field.Id}
        data-testid={`form-field-${field.Id}`}
      >
        {React.cloneElement(formField, {
          name: `${field.Id}`,

          onChange: (e, value) => {
            const changeEventType = IsChangeEvent(field);

            const nextVal =
              changeEventType === "true"
                ? GetValue(field, e, value)
                : field.FieldType === FieldTypes.Radio
                  ? value?.value
                  : changeEventType === "multiSelect"
                    ? e?.target?.value
                    : field.FieldType === FieldTypes.file
                      ? e
                      : value && typeof value === "object" && "value" in value
                        ? value.value
                        : e?.currentTarget?.value ?? e?.target?.value;

            const isTextLike = isTextLikeField(field.FieldType);

            const isDate = field.FieldType === FieldTypes.Date;
            const normalized =
              isDate && nextVal instanceof Date
                ? nextVal.toISOString()
                : nextVal;

            setFieldValue(`${field.Id}`, normalized, false);

            if (!isTextLike) {
              SetDataWithSameApiName(field, normalized);
            }
          },

          onBlur: (e) => {
            setFieldTouched(`${field.Id}`, true, false);

            const isTextLike = isTextLikeField(field.FieldType);

            if (isTextLike) {
              const latestVal = values[`${field.Id}`];
              SetDataWithSameApiName(field, latestVal);
            }
          },
        })}
      </div>
    );
  };

  return (
    <div className="form-fields">
      <Formik
        enableReinitialize={true}
        initialValues={useMemo(() => {
          return InitializeForm(currentSubForm);
        }, [currentSubForm])}
      >
        {({
          handleSubmit,
          handleChange,
          handleBlur,
          values,
          touched,
          errors,
          setFieldValue,
          setFieldTouched,
          isSubmitting,
          submitForm,
          setSubmitting,
        }) => (
          <form
            onSubmit={handleSubmit}
            className="d-flex flex-column h-100"
            data-testid="form"
          >
            {!IsView && (
              <TaskFormHeader
                values={values}
                isSubmitting={isSubmitting}
                setSubmitting={setSubmitting}
                ResetFormSection={ResetFormSection}
                AutoPopulateSubForm={AutoPopulateSubForm}
                AutoPopulateCount={AutoPopulateCount}
                SaveSubForm={SaveSubForm}
                FormName={currentSubForm.FormName}
                FormLabel={formLabel}
                taskId={taskId}
                formStatusMap={formStatusMap}
              />
            )}
            <div className="overflow-auto form-fields-main" ref={scrollerRef}>
              {(() => {
                const ordered = (formFields ?? []).filter(f => f.IsVisible).slice().sort(byOrder);
                const units = buildUnits(ordered);

                return units.map((unit) => {
                  if (!isParent(unit[0])) {
                    const field = unit[0];
                    return renderField(field, values, setFieldValue, setFieldTouched);
                  }

                  const parent = unit[0];
                  const children = unit.slice(1);
                  if (children.length === 0) return null;

                  const isCollapsed = !expanded.has(parent.Id);
                  const parentTitle =
                    (parent.LocalLabel && parent.LocalLabel.trim()) ||
                    (parent.DefaultLabel && parent.DefaultLabel.trim()) ||
                    "Untitled sub-section";

                  return (
                    <div
                      id={`subsection-${parent.Id}`}
                      tabIndex={-1}
                      key={`subsec-${parent.Id}`}
                      className={`form-field form-subsection ${isCollapsed ? "" : "expanded"}`}
                      data-testid={`subsection-${parent.Id}`}
                    >
                       <div
                          ref={setHeaderRef(parent.Id)}
                          className="d-flex align-items-center justify-content-between p-2 subsection-header"
                          role="button"
                          tabIndex={0}
                          aria-expanded={!isCollapsed}
                          onClick={() => toggle(parent.Id)}
                          onKeyDown={(e) => {
                            if (e.key === "Enter" || e.key === " ") {
                              e.preventDefault();
                              toggle(parent.Id);
                            }
                          }}
                        >
                        <strong>
                          {parentTitle}
                        </strong>

                         <span
                          aria-hidden="true"
                          style={{ display: "inline-flex", pointerEvents: "none" }}
                        >
                          <FluentUiIcon
                            name={isCollapsed ? "ChevronDownRegular" : "ChevronUpRegular"}
                            color={"#424242"}
                            size={20}
                          />
                        </span>
                      </div>

                      {!isCollapsed && (
                        <div
                          id={`subsection-body-${parent.Id}`}
                          className="subsection-body"
                        >
                          {children.map((field) => {
                            return renderField(field, values, setFieldValue, setFieldTouched);
                          })}
                        </div>
                      )}
                    </div>
                  );
                });
              })()}
            </div>
            <TaskFormFooter
              totalForms={totalForms}
              currentSubForm={currentSubForm}
              SaveSubForm={SaveSubForm}
              values={values}
              isSubmitting={isSubmitting}
              setSubmitting={setSubmitting}
              IsView={IsView}
            />
          </form>
        )}
      </Formik>
    </div>
  );
};

TaskFormFields.propTypes = {
  currentSubForm: PropTypes.shape({
    Fields: PropTypes.arrayOf(
      PropTypes.shape({
        LocalLabel: PropTypes.string,
        DefaultLabel: PropTypes.string,
        IsRequired: PropTypes.bool,
        Id: PropTypes.string.isRequired,
        IsDisabled: PropTypes.bool,
        IsVisible: PropTypes.bool,
        FieldType: PropTypes.oneOf(Object.values(FieldTypes)).isRequired,
        Files: PropTypes.array,
        LookUpValues: PropTypes.string,
        Table: PropTypes.shape({
          JsonDataFlag: PropTypes.bool,
          JsonDataId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
          JsonDataGridDefinitionList: PropTypes.array,
          JsonDataFilter: PropTypes.object,
          JsonDataNoEdit: PropTypes.bool,
        }),
      })
    ).isRequired,
    IsEditable: PropTypes.bool,
    IsMandatory: PropTypes.bool,
    FormName: PropTypes.string,
  }).isRequired,
  SaveSubForm: PropTypes.func.isRequired,
  totalForms: PropTypes.number.isRequired,
  ResetFormSection: PropTypes.func.isRequired,
  SetDataWithSameApiName: PropTypes.func.isRequired,
  AutoPopulateSubForm: PropTypes.func.isRequired,
  AutoPopulateCount: PropTypes.number.isRequired,
  handleCustomFormulaButtonClick: PropTypes.func,
  IsView: PropTypes.bool.isRequired,
  submitClicked: PropTypes.bool.isRequired,
  taskId: PropTypes.string.isRequired,
  formLabel: PropTypes.string.isRequired,
  formStatusMap: PropTypes.object,
};

export default TaskFormFields;
